/**
 * ProductAutocomplete — autocomplétion produit pour la saisie réception.
 *
 * Spec :
 *   - Catalog cache module-scope (chargé une seule fois /api/receptions/catalog-light)
 *   - Normalisation : UPPERCASE + trim + dedup espaces + retrait caractères spéciaux
 *   - Matching : exact normalisé > startsWith > contains (tri pertinence)
 *   - Si input = chiffres uniquement et pas de match → erreur "Produit non reconnu"
 *   - Si pas de match (texte) → badge "Nouveau produit", validation autorisée
 *   - Debounce 300ms, max 10 résultats
 *
 * Usage :
 *   <ProductAutocomplete
 *     value={text}
 *     onChange={(text, matchedProduct, status) => ...}
 *     placeholder="Désignation produit"
 *   />
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Input } from '../../ui/input';
import { CheckCircle2, AlertTriangle, Sparkles, Search, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('dermascan_token')}` });

// ───────── Normalisation ─────────
export const normalize = (s) =>
  (s || '')
    .toString()
    .toUpperCase()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[^A-Z0-9 ]/g, ''); // garde lettres, chiffres, espaces

// Version "compacte" : supprime aussi les espaces (pour matching plus tolérant)
const compact = (s) => normalize(s).replace(/\s/g, '');

const isDigitsOnly = (s) => /^\d+$/.test((s || '').trim());

// ───────── Catalog cache (module-scope, partagé entre composants) ─────────
let _catalogCache = null;
let _catalogLoadingPromise = null;

const getCatalog = async () => {
  if (_catalogCache) return _catalogCache;
  if (_catalogLoadingPromise) return _catalogLoadingPromise;
  _catalogLoadingPromise = (async () => {
    try {
      const r = await axios.get(`${API}/receptions/catalog-light`, { headers: auth() });
      const items = (r.data?.items || []).map((p) => ({
        ...p,
        _norm: normalize(p.name || ''),
        _compact: compact(p.name || ''),
      }));
      _catalogCache = items;
      return items;
    } catch (e) {
      _catalogLoadingPromise = null;
      throw e;
    }
  })();
  return _catalogLoadingPromise;
};

// Pré-charge le catalogue en arrière-plan (à appeler depuis le tab parent au montage)
export const preloadCatalog = () => {
  getCatalog().catch(() => {});
};

// ───────── Recherche ─────────
const searchCatalog = (catalog, query, limit = 10) => {
  const q = normalize(query);
  const qc = compact(query);
  if (!qc) return [];
  const exact = [];
  const starts = [];
  const contains = [];
  for (const p of catalog) {
    if (!p._norm) continue;
    // exact normalisé (priorité 1)
    if (p._norm === q || p._compact === qc) {
      exact.push(p);
    }
    // startsWith (priorité 2)
    else if (p._norm.startsWith(q) || p._compact.startsWith(qc)) {
      starts.push(p);
    }
    // contains (priorité 3)
    else if (p._norm.includes(q) || p._compact.includes(qc)) {
      contains.push(p);
    }
    if (exact.length + starts.length + contains.length >= limit * 3) break;
  }
  // Tri alphabétique secondaire
  starts.sort((a, b) => a._norm.localeCompare(b._norm));
  contains.sort((a, b) => a._norm.localeCompare(b._norm));
  return [...exact, ...starts, ...contains].slice(0, limit);
};

// Recherche "code" : si input chiffres, cherche un produit dont le nom commence
// par ces chiffres (cas pratique : EAN/CIP/code interne intégré au nom)
const searchByCode = (catalog, code) => {
  const c = (code || '').trim();
  if (!c || !isDigitsOnly(c)) return [];
  return catalog
    .filter((p) => (p._compact || '').includes(c))
    .slice(0, 10);
};

// ───────── Status helpers ─────────
const STATUS = {
  empty: { label: '', cls: '' },
  recognized: { label: 'Reconnu', icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-700' },
  suggestion: { label: 'Suggestion', icon: Search, cls: 'bg-blue-100 text-blue-700' },
  unrecognized: { label: 'Produit non reconnu', icon: AlertTriangle, cls: 'bg-red-100 text-red-700' },
  newProduct: { label: 'Nouveau produit', icon: Sparkles, cls: 'bg-violet-100 text-violet-700' },
};

const ProductAutocomplete = ({
  value,
  onChange,
  placeholder = 'Désignation ou code produit',
  className = '',
  autoFocus = false,
  testId = 'product-autocomplete',
}) => {
  const [text, setText] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [matched, setMatched] = useState(null);
  const [status, setStatus] = useState('empty');
  const debounceRef = useRef(null);
  const blurTimerRef = useRef(null);

  // Sync external value
  useEffect(() => { setText(value || ''); }, [value]);

  // Recherche debounced
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!text || text.length < 3) {
      setResults([]);
      setStatus('empty');
      setMatched(null);
      onChange?.(text, null, 'empty');
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const catalog = await getCatalog();
        const useCodeSearch = isDigitsOnly(text);
        const list = useCodeSearch
          ? searchByCode(catalog, text)
          : searchCatalog(catalog, text, 10);
        setResults(list);
        // Détection statut
        let newStatus = 'newProduct';
        let newMatched = null;
        if (list.length === 1 && (list[0]._norm === normalize(text) || list[0]._compact === compact(text))) {
          newStatus = 'recognized';
          newMatched = list[0];
        } else if (list.length > 0) {
          newStatus = 'suggestion';
        } else if (useCodeSearch) {
          newStatus = 'unrecognized';
        }
        setStatus(newStatus);
        setMatched(newMatched);
        onChange?.(text, newMatched, newStatus);
      } finally { setLoading(false); }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const handlePick = (p) => {
    setText(p.name);
    setMatched(p);
    setStatus('recognized');
    setOpen(false);
    onChange?.(p.name, p, 'recognized');
  };

  const StatusIcon = STATUS[status]?.icon;

  return (
    <div className={`relative ${className}`} data-testid={testId}>
      <Input
        value={text}
        onChange={(e) => { setText(e.target.value); setOpen(true); }}
        onFocus={() => { if (results.length) setOpen(true); }}
        onBlur={() => { blurTimerRef.current = setTimeout(() => setOpen(false), 180); }}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="text-sm pr-8"
        data-testid={`${testId}-input`}
      />
      {loading && (
        <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 animate-spin" />
      )}

      {/* Badge statut */}
      {status !== 'empty' && !loading && (
        <span
          className={`inline-flex items-center gap-1 mt-1 text-[10px] font-semibold px-2 py-0.5 rounded ${STATUS[status].cls}`}
          data-testid={`${testId}-status-${status}`}
        >
          {StatusIcon && <StatusIcon className="w-3 h-3" />}
          {STATUS[status].label}
        </span>
      )}

      {/* Dropdown suggestions */}
      {open && results.length > 0 && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-72 overflow-auto"
          onMouseDown={() => { if (blurTimerRef.current) clearTimeout(blurTimerRef.current); }}
          data-testid={`${testId}-dropdown`}
        >
          {results.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => handlePick(p)}
              className="w-full text-left px-3 py-2 hover:bg-purple-50 border-b border-zinc-100 last:border-b-0 transition-colors"
              data-testid={`${testId}-option-${p.id}`}
            >
              <div className="text-sm font-medium text-zinc-900">{p.name}</div>
              {p.brand && (
                <div className="text-xs text-zinc-500">
                  {p.brand}{p.category ? ` · ${p.category}` : ''}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductAutocomplete;
